import Button from "@material-ui/core/Button/Button";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import FormControl from "@material-ui/core/FormControl/FormControl";
import FormGroup from "@material-ui/core/FormGroup/FormGroup";
import FormLabel from "@material-ui/core/FormLabel/FormLabel";
import TextField from '@material-ui/core/TextField/TextField';
import Typography from "@material-ui/core/Typography/Typography";
import Save from '@material-ui/icons/Save';
import { FilesRequestFactory } from "model/autogen/FilesController";
import * as React from "react";
import { ChangeEvent } from "react";
import { isArrayNullOrEmpty } from "utils/array";
import { FaqsRequestFactory } from "../../model/autogen/FaqsController";
import { FrequentlyAskedQuestionModel } from "../../model/autogen/FrequentlyAskedQuestionModel";
import { TagModel } from "../../model/autogen/TagModel";
import { UserLevel } from "../../model/autogen/UserLevel";
import { withEditorPage } from "../../pages/editor-page";
import { strIsNullOrEmpty } from "../../utils/string";
import { LinkCollectionEditor } from "../link-collection-editor";
import TagListEditor from "../material/tag-list-editor";
import { IAuthContextProps, withAuthContext } from "../with-auth-context";
import { ISnackbarContextProps, withSnackbar } from "../with-snackbar";
import { findTagsHelper, resolveTagsHelper } from "./form-state-helper";
import { IContentForm } from "./icontent-form";
import { RTEField } from "./rte-field";
import { UploadSection } from "./upload-section";

export interface IFaqProps extends IContentForm<FrequentlyAskedQuestionModel> {
}

export interface IFaqState extends FrequentlyAskedQuestionModel {
    isSaving: boolean;
    isUploadingFiles: boolean;
}

type FaqFormComponentProps = IFaqProps & IAuthContextProps & ISnackbarContextProps;

class FaqFormComponent extends React.Component<FaqFormComponentProps, IFaqState> {
    private counter: number;
    constructor(props: FaqFormComponentProps) {
        super(props);
        this.counter = 0;
        this.state = { isSaving: false, isUploadingFiles: false, ...props.item };
    }

    private onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ [e.target.name]: e.target.value } as any);
    }

    private onSave = async (e: any) => {
        const { isSaving, ...payload } = this.state;
        const { callApi, showSnackbarMessage } = this.props;
        this.setState({ isSaving: true });
        try {
            const result = await callApi((fetcher) => (payload.id > 0 ? FaqsRequestFactory.update : FaqsRequestFactory.create)(fetcher, payload));
            showSnackbarMessage(payload.id > 0 ? 'FAQ Saved' : `New FAQ saved (${result.documentId})`);
            
            this.props.onSaveCompleted();
        } catch (e) {
            showSnackbarMessage(`Error saving FAQ: ${e}`);
            this.setState({ isSaving: false });
        }
    }
    private onTagsChanged = (tags: TagModel[]) => {
        this.setState({ createTags: tags });
    }
    private onSearchTags = (query: string, isResourceOnly: boolean) => {
        return findTagsHelper(this.props.callApi, query, isResourceOnly);
    }
    private onResolveTags = (tags: string[]) => {
        return resolveTagsHelper(this.props.callApi, tags);
    }
    private onFileDrop = async (acceptedFiles: File[], rejectedFiles: File[]) => {
        const { callApi } = this.props;
        try {
            if (!isArrayNullOrEmpty(acceptedFiles)) {
                this.setState({ isUploadingFiles: true });
                const result = await callApi(f => FilesRequestFactory.upload(f, { file: acceptedFiles[0] }));
                const { assignFiles } = this.state;
                this.setState({ isUploadingFiles: false, assignFiles: [...(assignFiles || []), result] });
            }
        } catch (e) {
            this.props.showSnackbarMessage(`Failed to upload file: ${e}`);
            this.setState({ isUploadingFiles: false });
        }
    }
    private onLinkChanged = (index: number, title: string, url: string) => {
        const { links } = this.state;
        if (links[index]) {
            links[index].title = title;
            links[index].url = url;
            this.setState({ links });
        }
    }
    private onAddLink = () => {
        const { links } = this.state;
        links.push({
            id: (this.counter--),
            title: "New Link",
            icon: "web",
            url: "https://"
        });
        this.setState({ links });
    }
    private onRemoveLink = (index: number) => {
        const { links } = this.state;
        links.splice(index, 1);
        this.setState({ links });
    }
    public render(): JSX.Element {
        const { answer, title, /*tags, */isSaving, isUploadingFiles, assignFiles, files } = this.state;
        const cannotSave = isSaving || strIsNullOrEmpty(answer) || strIsNullOrEmpty(title);
        return <>
            <FormControl component={"fieldset" as "div"} fullWidth={true}>
                <Typography variant="h5">{!this.props.item.id ? 'New' : ''} FAQ Details</Typography>
                <FormGroup>
                    <TextField label="Question" rows={2} rowsMax={4} multiline={true} fullWidth={true} name="title" value={title} onChange={this.onTextChange} disabled={isSaving} />
                    <FormControl>
                        <FormLabel>Answer</FormLabel>
                        <RTEField name="answer" value={answer} onChange={this.onTextChange} />
                    </FormControl>
                    <UploadSection onFileDrop={this.onFileDrop} isUploadingFiles={isUploadingFiles} files={files} assignFiles={assignFiles} />
                    <FormControl>
                        <FormLabel>Tags</FormLabel>
                        <TagListEditor isResourceOnly={false} initialTags={this.props.item.tags} onTagsChanged={this.onTagsChanged} onFetchTags={this.onSearchTags} onResolveTags={this.onResolveTags} />
                    </FormControl>
                    <FormGroup>
                        <Typography variant="h6">Links</Typography>
                        <LinkCollectionEditor items={this.state.links} onAdd={this.onAddLink} onChange={this.onLinkChanged} onRemove={this.onRemoveLink} />
                    </FormGroup>
                    <Button variant="contained" fullWidth={true} onClick={this.onSave} disabled={isSaving || cannotSave} color="primary">
                        {isSaving ? <CircularProgress size={24} /> : <Save />}{isSaving ? " Saving ..." : " Save"}
                    </Button>
                </FormGroup>
            </FormControl>
        </>;
    }
}

// HACK: wrapper to workaround the fact that our current codegen doesn't support argument default values, so there's a signature mismatch
const faqGet = (fetcher: (url: string, init: RequestInit) => Promise<Response>, id: number) => FaqsRequestFactory.getById(fetcher, id, false);

export const FaqForm = withSnackbar(withAuthContext(FaqFormComponent, UserLevel.SystemUser)) as React.ComponentType<IFaqProps>;

export const FaqEditPage = withAuthContext(withEditorPage(FaqForm, faqGet), UserLevel.SystemUser) as React.ComponentType<IFaqProps>;


// WEBPACK FOOTER //
// ./src/components/forms/faq.tsx